/**
 * useCreateVersion — State machine driving the Create New Version flow.
 *
 * The UI is a single continuous chat view. Phases control which extra
 * elements render inline (approach cards, thinking, review artifacts, etc.)
 * but the chat + input bar remain visible throughout.
 *
 * Phases:
 *  1. approach   – greeting + approach cards shown inline, input bar visible
 *  2. chat       – conversational planner (draft endpoint loops until ready)
 *  3. planning   – "Thinking..." animation shown inline
 *  4. running    – workflow started, progress shown inline
 *  5. review     – review_artifacts available (insights + changes)
 *  6. saving     – apply endpoint called
 *  7. done       – version created confirmation
 *  8. error      – something went wrong
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import versionWorkflowService, {
  type ApplyAction,
  type DraftRequest,
  type DraftResponse,
  type PlannerMessage,
  type DraftPlan,
  type WorkflowStatus,
  type ReviewArtifacts,
  type ABTestSummary,
} from '@/lib/api/versionWorkflow';
import performanceService, {
  type RubricOverviewItem,
} from '@/lib/api/performance';

export type Phase =
  | 'approach'
  | 'chat'
  | 'confirm'
  | 'planning'
  | 'running'
  | 'review'
  | 'scores_ready'
  | 'saving'
  | 'ab_running'
  | 'ab_results'
  | 'done'
  | 'error';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Optional override sent to planner instead of content */
  plannerContent?: string;
  /** Timestamp for display ordering */
  ts: number;
}

export interface CreateVersionState {
  phase: Phase;
  intent: 'score_driven' | 'targeted_revision' | null;
  messages: ChatMessage[];
  plannerState: Record<string, unknown> | null;
  draftPlan: DraftPlan | null;
  batchId: string | null;
  workflowStatus: WorkflowStatus | null;
  reviewArtifacts: ReviewArtifacts | null;
  abTestSummary: ABTestSummary | null;
  newVersionNumber: number | null;
  error: string | null;
  isSending: boolean;
  rubricNames: string[];
  /** Full rubric data from eval overview — used for the inline scores card */
  evalRubrics: RubricOverviewItem[];
  /** Label for the eval set (e.g. "Custom Eval Scores") */
  evalSetName: string | null;
  /** Whether eval insights are currently being fetched */
  insightsLoading: boolean;
  /** LLM-generated suggestion labels for pills (score_driven flow) */
  suggestionLabels: string[];
}

let _msgId = 0;
function nextMsgId(): string {
  return `msg-${++_msgId}-${Date.now()}`;
}

function buildWorkflowStatusSnapshot(
  organizationId: string,
  batchId: string,
  status: string,
): WorkflowStatus {
  return {
    batch_id: batchId,
    organization_id: organizationId,
    status,
    dataset_names: [],
    optimization_run_id: null,
    prompt_name: null,
    new_prompt_version: null,
    diff_summary: null,
    ab_test_summary: null,
    aggregate_metrics: null,
    workflow_config: null,
    baseline_scores: null,
    post_change_scores: null,
    review_artifacts: null,
    started_at: null,
    completed_at: null,
    error: null,
  };
}

export function useCreateVersion() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id ?? '';

  const [state, setState] = useState<CreateVersionState>({
    phase: 'approach',
    intent: null,
    messages: [],
    plannerState: null,
    draftPlan: null,
    batchId: null,
    workflowStatus: null,
    reviewArtifacts: null,
    abTestSummary: null,
    newVersionNumber: null,
    error: null,
    isSending: false,
    rubricNames: [],
    evalRubrics: [],
    evalSetName: null,
    insightsLoading: false,
    suggestionLabels: [],
  });

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const directSessionInFlightRef = useRef(false);
  const pollSessionRef = useRef(0);
  const pollRequestInFlightRef = useRef(false);
  const pollAbortControllerRef = useRef<AbortController | null>(null);
  const activeBatchIdRef = useRef<string | null>(null);

  // ── helpers ──────────────────────────────────────────────────────────

  const patch = useCallback(
    (partial: Partial<CreateVersionState>) =>
      setState((prev) => ({ ...prev, ...partial })),
    [],
  );

  // ── fetch rubric names (for option pills) ───────────────────────────

  const loadRubricNames = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await performanceService.getOverview(orgId, { range: '7d' });
      if (res.success && res.data) {
        const names = res.data.rubrics.map((r) => r.name);
        patch({ rubricNames: names });
      }
    } catch {
      // Non-critical — pills will just be empty
    }
  }, [orgId, patch]);

  // ── fetch eval rubrics for insights card (score_driven) ───────────

  const loadEvalInsights = useCallback(async (): Promise<RubricOverviewItem[]> => {
    if (!orgId) return [];
    patch({ insightsLoading: true });
    try {
      // Try custom eval scores from simulation runs first
      const res = await performanceService.getOverview(orgId, {
        range: '7d',
        source: 'custom',
        data_source: 'eval',
      });

      if (res.success && res.data && res.data.rubrics.length > 0) {
        const rubrics = res.data.rubrics;
        patch({
          evalRubrics: rubrics,
          evalSetName: res.data.eval_set_name ?? null,
          rubricNames: rubrics.map((r) => r.name),
          insightsLoading: false,
        });
        return rubrics;
      }

      // Fallback: baseline eval scores from Langfuse evaluators
      const fallback = await performanceService.getOverview(orgId, {
        range: '7d',
        source: 'baseline',
        data_source: 'eval',
      });

      if (fallback.success && fallback.data && fallback.data.rubrics.length > 0) {
        const rubrics = fallback.data.rubrics;
        patch({
          evalRubrics: rubrics,
          evalSetName: fallback.data.eval_set_name ?? null,
          rubricNames: rubrics.map((r) => r.name),
          insightsLoading: false,
        });
        return rubrics;
      }

      // No eval data available — clear loading, pills will be empty
      patch({ insightsLoading: false });
      return [];
    } catch {
      // Non-critical — card won't render, fall back to normal planner flow
      patch({ insightsLoading: false });
      return [];
    }
  }, [orgId, patch]);

  // ── internal: call the planner and handle response ──────────────────

  const callPlanner = useCallback(
    async (
      allMessages: PlannerMessage[],
      plannerState: Record<string, unknown> | null,
      intent: 'score_driven' | 'targeted_revision',
      evalScores?: { name: string; score: number }[],
    ) => {
      const body: DraftRequest = {
        messages: allMessages,
        state: plannerState,
        optimization_intent: intent,
        pause_before_apply: true,
        enable_ab_test: true,
        auto_discover_datasets: true,
        ...(evalScores && evalScores.length > 0 ? { eval_scores: evalScores } : {}),
      };

      const res = await versionWorkflowService.draft(orgId, body);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to get planner response');
      }
      return res.data;
    },
    [orgId],
  );

  const invalidatePollingSession = useCallback(() => {
    pollSessionRef.current += 1;
    pollRequestInFlightRef.current = false;
    if (pollAbortControllerRef.current) {
      pollAbortControllerRef.current.abort();
      pollAbortControllerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    invalidatePollingSession();
  }, [invalidatePollingSession]);

  // ── poll workflow status ────────────────────────────────────────────

  const startPolling = useCallback(
    (batchId: string) => {
      stopPolling();
      activeBatchIdRef.current = batchId;
      const sessionToken = pollSessionRef.current + 1;
      pollSessionRef.current = sessionToken;

      const isCurrentPollSession = () =>
        pollSessionRef.current === sessionToken &&
        activeBatchIdRef.current === batchId;

      const poll = async () => {
        if (!orgId || !isCurrentPollSession() || pollRequestInFlightRef.current) {
          return;
        }

        pollRequestInFlightRef.current = true;
        const controller = new AbortController();
        pollAbortControllerRef.current = controller;
        try {
          const res = await versionWorkflowService.getWorkflowStatus(
            orgId,
            batchId,
            controller.signal,
          );
          if (
            controller.signal.aborted ||
            !isCurrentPollSession() ||
            !res.success ||
            !res.data
          ) {
            return;
          }

          const ws = res.data;
          patch({ workflowStatus: ws });

          if (ws.status === 'awaiting_review' && ws.review_artifacts) {
            stopPolling();
            patch({ reviewArtifacts: ws.review_artifacts, phase: 'review' });
          } else if (ws.status === 'scores_ready') {
            stopPolling();
            patch({
              workflowStatus: ws,
              reviewArtifacts: ws.review_artifacts ?? null,
              phase: 'scores_ready',
            });
          } else if (ws.status === 'failed') {
            stopPolling();
            patch({ error: ws.error || 'Workflow failed', phase: 'error' });
          } else if (ws.status === 'workflow_complete_no_changes') {
            stopPolling();
            patch({
              error: 'The optimization found no changes to propose. Try providing more specific feedback or adjusting your focus areas.',
              phase: 'error',
            });
          } else if (
            ws.status.startsWith('workflow_complete') ||
            ws.status === 'completed'
          ) {
            stopPolling();
            if (!ws.new_prompt_version && !ws.review_artifacts) {
              patch({
                error:
                  'No changes could be proposed within the targeted scope. Try broadening your focus or targeting different areas.',
                phase: 'error',
              });
            } else if (ws.ab_test_summary) {
              patch({ abTestSummary: ws.ab_test_summary, phase: 'ab_results' });
            } else {
              const versionNum = ws.new_prompt_version
                ? parseInt(ws.new_prompt_version, 10) || null
                : null;
              patch({ newVersionNumber: versionNum, phase: 'done' });
            }
          }
        } catch {
          // Silent — will retry on next interval
        } finally {
          if (pollAbortControllerRef.current === controller) {
            pollAbortControllerRef.current = null;
          }
          if (isCurrentPollSession()) {
            pollRequestInFlightRef.current = false;
          }
        }
      };

      poll();
      pollingRef.current = setInterval(poll, 5000);
    },
    [orgId, patch, stopPolling],
  );

  // ── start the optimization workflow ─────────────────────────────────

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startWorkflow = useCallback(
    async (workflowRequest: Record<string, unknown>) => {
      if (!orgId) return;
      patch({ phase: 'running' });

      try {
        const res = await versionWorkflowService.startWorkflow(orgId, workflowRequest);
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to start workflow');
        }

        const batchId = res.data.batch_id;
        patch({
          batchId,
          workflowStatus: buildWorkflowStatusSnapshot(orgId, batchId, res.data.status),
        });
        startPolling(batchId);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start workflow';
        patch({ error: message, phase: 'error' });
      }
    },
    [orgId, patch, startPolling],
  );

  const generateChanges = useCallback(
    async (workflowRequest: Record<string, unknown>) => {
      if (!orgId) return;
      patch({ phase: 'running' });

      try {
        const res = await versionWorkflowService.generateChanges(orgId, workflowRequest);
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to generate prompt changes');
        }

        const batchId = res.data.batch_id;
        patch({
          batchId,
          workflowStatus: buildWorkflowStatusSnapshot(orgId, batchId, res.data.status),
        });
        startPolling(batchId);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to generate prompt changes';
        patch({ error: message, phase: 'error' });
      }
    },
    [orgId, patch, startPolling],
  );

  const startDirectSession = useCallback(async () => {
    if (!orgId || state.isSending || directSessionInFlightRef.current) return;

    directSessionInFlightRef.current = true;

    setState((prev) => ({
      ...prev,
      intent: 'score_driven',
      phase: 'running',
      isSending: true,
      error: null,
      suggestionLabels: [],
      messages: [
        ...prev.messages,
        {
          id: nextMsgId(),
          role: 'assistant' as const,
          content:
            "Running simulated buyer conversations now. I’ll score the results and suggest changes if needed.",
          ts: Date.now(),
        },
      ],
    }));

    try {
      await startWorkflow({
        mode: 'batch_simulation',
        optimization_intent: 'score_driven',
        auto_discover_datasets: true,
        score_threshold: 60,
        max_changes: 5,
        pause_before_apply: true,
        enable_ab_test: true,
        run_custom_evaluators: true,
      });
    } finally {
      directSessionInFlightRef.current = false;
      patch({ isSending: false });
    }
  }, [orgId, patch, startWorkflow, state.isSending]);

  const confirmDraftPlan = useCallback(async () => {
    if (!state.draftPlan) return;
    await generateChanges(state.draftPlan.workflow_request);
  }, [generateChanges, state.draftPlan]);

  const handlePlannerResponse = useCallback(
    (
      data: DraftResponse,
      intent: 'score_driven' | 'targeted_revision',
    ) => {
      setState((prev) => ({
        ...prev,
        isSending: false,
        plannerState: data.state,
        suggestionLabels: data.suggestion_labels ?? [],
        messages: [
          ...prev.messages,
          { id: nextMsgId(), role: 'assistant' as const, content: data.assistant_message, ts: Date.now() },
        ],
        ...(data.status === 'ready' && data.draft_plan
          ? { draftPlan: data.draft_plan, phase: (intent === 'targeted_revision' ? 'confirm' : 'planning') as Phase }
          : {}),
      }));

      if (intent === 'score_driven' && data.status === 'ready' && data.draft_plan) {
        const wfReq = data.draft_plan.workflow_request;
        setTimeout(() => {
          startWorkflow(wfReq);
        }, 3000);
      }
    },
    [startWorkflow],
  );

  // ── approach selection ──────────────────────────────────────────────

  const selectApproach = useCallback(
    (intent: 'score_driven' | 'targeted_revision') => {
      const label =
        intent === 'score_driven' ? 'Review Insights' : 'Share Your Feedback';
      const plannerMsg =
        intent === 'score_driven'
          ? 'Based on the eval scores provided, start by identifying the weakest scoring areas and briefly explain what gaps you see. Then ask 1-2 specific questions about how the customer currently handles those areas in practice — for example, how they handle objections, what their ideal flow looks like, or what context the agent is missing. These questions should help you understand the real-world process so you can patch the gaps. Do not open with filler like "Happy to help" — start directly with your analysis.'
          : undefined;

      setState((prev) => ({
        ...prev,
        intent,
        phase: 'chat',
        isSending: true,
        messages: [
          ...prev.messages,
          { id: nextMsgId(), role: 'user' as const, content: label, ...(plannerMsg ? { plannerContent: plannerMsg } : {}), ts: Date.now() },
        ],
      }));

      if (intent === 'score_driven') {
        // Fetch eval insights, then call planner with scores to get
        // an assistant message + LLM-generated suggestion labels.
        (async () => {
          try {
            const rubrics = await loadEvalInsights();
            const evalScores = rubrics
              .filter((r) => r.score_7d != null)
              .map((r) => ({ name: r.name, score: r.score_7d as number }));

            const data = await callPlanner(
              [{ role: 'user', content: plannerMsg! }],
              null,
              intent,
              evalScores,
            );

            setState((prev) => ({
              ...prev,
              isSending: false,
              plannerState: data.state,
              suggestionLabels: data.suggestion_labels ?? [],
              messages: [
                ...prev.messages,
                { id: nextMsgId(), role: 'assistant' as const, content: data.assistant_message, ts: Date.now() },
              ],
              ...(data.status === 'ready' && data.draft_plan
                ? { draftPlan: data.draft_plan, phase: 'planning' as Phase }
                : {}),
            }));

            if (data.status === 'ready' && data.draft_plan) {
              setTimeout(() => {
                startWorkflow(data.draft_plan!.workflow_request);
              }, 3000);
            }
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            patch({ isSending: false, error: message, phase: 'error' });
          }
        })();
      } else {
        // targeted_revision — call planner immediately as before
        loadRubricNames();
        (async () => {
          try {
            const data = await callPlanner(
              [{ role: 'user', content: label }],
              null,
              intent,
            );
            handlePlannerResponse(data, intent);
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            patch({ isSending: false, error: message, phase: 'error' });
          }
        })();
      }
    },
    [callPlanner, handlePlannerResponse, loadRubricNames, loadEvalInsights, patch, startWorkflow],
  );

  // ── send a chat message to the planner ──────────────────────────────

  const sendMessage = useCallback(
    async (content: string) => {
      if (!orgId || state.isSending) return;

      const isFromApproach = state.phase === 'approach';
      const intent = state.intent || 'targeted_revision';

      setState((prev) => ({
        ...prev,
        phase: 'chat',
        isSending: true,
        intent,
        messages: [
          ...prev.messages,
          { id: nextMsgId(), role: 'user' as const, content, ts: Date.now() },
        ],
      }));

      if (isFromApproach) {
        loadRubricNames();
      }

      const allMessages: PlannerMessage[] = [
        ...state.messages.map((m) => ({ role: m.role, content: m.plannerContent ?? m.content })),
        { role: 'user' as const, content },
      ];

      try {
        const data = await callPlanner(allMessages, state.plannerState, intent);
        handlePlannerResponse(data, intent);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        patch({ isSending: false, error: message, phase: 'error' });
      }
    },
    [orgId, state.isSending, state.messages, state.plannerState, state.intent, state.phase, callPlanner, handlePlannerResponse, patch, loadRubricNames],
  );

  // ── apply reviewed prompt (save as staging) ─────────────────────────

  const applyPrompt = useCallback(
    async (
      updatedPrompt?: string,
      action: ApplyAction | boolean = 'save',
    ) => {
      if (!orgId || !state.batchId) return;
      const isLegacyMode = typeof action === 'boolean';
      const explicitAction = isLegacyMode ? null : action;
      const enableAbTest = isLegacyMode ? action : action === 'run_ab_test';
      const optimisticStatus =
        explicitAction === 'run_scores' && state.intent === 'targeted_revision'
          ? buildWorkflowStatusSnapshot(orgId, state.batchId, 'running_preview_experiments')
          : null;

      patch(
        optimisticStatus
          ? {
              phase:
                enableAbTest
                  ? 'ab_running'
                  : explicitAction === 'run_scores'
                    ? 'running'
                    : 'saving',
              workflowStatus: optimisticStatus,
            }
          : {
              phase:
                enableAbTest
                  ? 'ab_running'
                  : explicitAction === 'run_scores'
                    ? 'running'
                    : 'saving',
            },
      );

      try {
        const res = await versionWorkflowService.applyReviewedPrompt(orgId, state.batchId, {
          updated_prompt: updatedPrompt ?? null,
          action: explicitAction,
          enable_ab_test: isLegacyMode
            ? enableAbTest
            : explicitAction === 'run_ab_test'
              ? true
              : explicitAction === 'save'
                ? false
                : null,
        });

        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to apply prompt');
        }

        patch({
          workflowStatus: buildWorkflowStatusSnapshot(orgId, state.batchId, res.data.status),
        });
        startPolling(state.batchId);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save';
        patch({ error: message, phase: 'error' });
      }
    },
    [orgId, state.batchId, patch, startPolling],
  );

  // ── edit/delete proposed changes in review ─────────────────────────

  const removeChange = useCallback((changeId: string) => {
    setState((prev) => {
      if (!prev.reviewArtifacts) return prev;
      return {
        ...prev,
        reviewArtifacts: {
          ...prev.reviewArtifacts,
          proposed_changes: prev.reviewArtifacts.proposed_changes.filter(
            (c) => c.change_id !== changeId,
          ),
        },
      };
    });
  }, []);

  const updateChangeText = useCallback((changeId: string, newText: string) => {
    setState((prev) => {
      if (!prev.reviewArtifacts) return prev;
      return {
        ...prev,
        reviewArtifacts: {
          ...prev.reviewArtifacts,
          proposed_changes: prev.reviewArtifacts.proposed_changes.map((c) =>
            c.change_id === changeId ? { ...c, new_text: newText } : c,
          ),
        },
      };
    });
  }, []);

  // ── reset to start over ────────────────────────────────────────────

  const reset = useCallback(() => {
    stopPolling();
    directSessionInFlightRef.current = false;
    activeBatchIdRef.current = null;
    setState({
      phase: 'approach',
      intent: null,
      messages: [],
      plannerState: null,
      draftPlan: null,
      batchId: null,
      workflowStatus: null,
      reviewArtifacts: null,
      abTestSummary: null,
      newVersionNumber: null,
      error: null,
      isSending: false,
      rubricNames: [],
      evalRubrics: [],
      evalSetName: null,
      insightsLoading: false,
      suggestionLabels: [],
    });
  }, [stopPolling]);

  return {
    state,
    selectApproach,
    startDirectSession,
    confirmDraftPlan,
    sendMessage,
    applyPrompt,
    removeChange,
    updateChangeText,
    reset,
    stopPolling,
  };
}
